import { router } from 'expo-router';
import { useState } from 'react';
import { KeyboardAvoidingView, Platform, StyleSheet, TextInput, View } from 'react-native';
import CurrencyInput from 'react-native-currency-input';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import BrandedButton from '@/components/buttons/BrandedButton';
import InfoButton from '@/components/buttons/InfoButton';
import Text from '@/components/Text';
import { ICAC_NPO_ID } from '@/constants/config';
import { useColor } from '@/hooks/useColor';
import { i18n } from '@/utils/i18n';

interface DonateFooterProps {
  npoId: string;
  npoName: string;
}

const DonateFooter = ({ npoId, npoName }: DonateFooterProps) => {
  const colors = useColor();

  const { bottom } = useSafeAreaInsets();
  const [amountToDonate, setAmountToDonate] = useState<number | null>(null);

  const isIcacNpo = npoId === ICAC_NPO_ID;

  const yellowBrand = '#EBB004';

  const handleNavigate = (isIcac: boolean) => {
    router.navigate({
      pathname: '/donation',
      params: {
        npoId: npoId,
        npoName: npoName,
        amount: amountToDonate ?? 0,
        isIcac: String(isIcac)
      }
    });
  };

  const renderAmountInput = () => (
    <View style={[styles.default, styles.currencyContainer, { borderColor: colors.progressUncompleted }]}>
      <CurrencyInput
        prefix={'$'}
        delimiter={','}
        precision={0}
        maxLength={10}
        onChangeValue={(value) => setAmountToDonate(value)}
        returnKeyLabel="done"
        returnKeyType="done"
        value={amountToDonate}
        renderTextInput={(props) => (
          <TextInput
            {...props}
            placeholder={i18n.t('donation.donation_amount')}
            placeholderTextColor={colors.placeholderText}
            style={[styles.inputPadding, Platform.OS === 'web' && ({ outlineStyle: 'none' } as any)]}
          />
        )}
      />
    </View>
  );

  return (
    <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <View style={[styles.donateFooter, { paddingBottom: bottom === 0 || Platform.OS === 'web' ? 25 : bottom }]}>
        {isIcacNpo ? (
          <>
            <View style={styles.icacButtonRow}>{renderAmountInput()}</View>

            <View style={styles.icacButtonRow}>
              <BrandedButton
                style={styles.btnStyle}
                onPress={() => handleNavigate(false)}
                backGroundColor={yellowBrand}
                borderColor={yellowBrand}>
                {i18n.t('donation.donate_organization')}
              </BrandedButton>

              <InfoButton
                title={i18n.t('donation.icac_organization_info_title')}
                message={i18n.t('donation.icac_organization_info_subtitle')}
                type="modal"
              />
            </View>

            <View style={styles.icacButtonRow}>
              <BrandedButton
                style={styles.btnStyle}
                onPress={() => handleNavigate(true)}
                backGroundColor="transparent"
                borderColor={yellowBrand}
                borderWidth={1}
                textStyles={{ color: yellowBrand }}>
                {i18n.t('donation.donate_icac')}
              </BrandedButton>

              <InfoButton
                title={i18n.t('donation.icac_info_title')}
                message={i18n.t('donation.icac_info_subtitle')}
                type="modal"
              />
            </View>
          </>
        ) : (
          <>
            <View style={styles.subtitleContainer}>
              <Text style={styles.donateSubtitle}>{i18n.t('donation.footer_subtitle')}</Text>

              <InfoButton title={i18n.t('donation.donate')} />
            </View>

            <View style={styles.donateButtonRow}>
              {renderAmountInput()}

              <BrandedButton
                style={styles.btnStyle}
                onPress={() => handleNavigate(false)}
                backGroundColor={yellowBrand}
                borderColor={yellowBrand}>
                {i18n.t('donation.donate')}
              </BrandedButton>
            </View>
          </>
        )}
      </View>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  default: {
    justifyContent: 'center',
    borderWidth: 1,
    borderRadius: 10
  },

  currencyContainer: {
    flex: 1,
    height: 44
  },

  inputPadding: {
    paddingHorizontal: 16
  },

  donateFooter: {
    backgroundColor: 'white',
    flexDirection: 'column',
    width: '100%',
    paddingHorizontal: 18,
    paddingTop: 20,
    shadowColor: 'black',
    shadowOffset: {
      width: 0,
      height: -2
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 5
  },

  subtitleContainer: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
    marginBottom: 4
  },

  donateSubtitle: {
    color: 'black',
    opacity: 0.5,
    fontSize: 13
  },

  donateButtonRow: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 12
  },

  btnStyle: {
    flex: 1
  },

  icacButtonRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 12
  }
});

export default DonateFooter;
